import { PayloadAction } from '@reduxjs/toolkit';
import {
    ICreateTagAction,
    IDeleteCompanyMailboxAction,
    IDeleteTagAction,
    IFetchCompanyInfoAction,
    IPostConnectGmailAction,
    IPostConnectOutlookAction,
    IUpdateTagAction,
    IUpdateCompanySettingsAction,
    ICreateRejectReasonAction,
    IUpdateRejectReasonAction,
    IDeleteRejectReasonAction,
    organizationActions,
    organizationSelectors,
} from '../redux';
import {
    createTag,
    deleteCompanyMailbox,
    deleteTag,
    fetchCompanyInfo,
    fetchTags,
    fetchCompanySettings,
    postConnectGmail,
    postConnectOutlook,
    updateTag,
    updateCompanySettings,
    createRejectReason,
    updateRejectReason,
    deleteRejectReason,
} from '../../apis/organization';
import { put, call, select } from 'redux-saga/effects';
import { handleLanguage } from '../../user/saga/saga';
import { userActions, userSelectors } from '../../user/redux';
import {
    convertServerTagsToClient,
    getCompanyInfoFromServerFormat,
    getCompanySettingsFromServerFormat,
    getNewTagOrUpdatedTagFromServerFormat,
    convertCompanyInfoToServerFormat,
} from './manager';
import { ITag, SnackbarUtils } from '../../../common';
import { IOption } from '../../../base';
import i18next from 'i18next';

/**
 * Surfaces the server's field error for a reject reason (e.g. the duplicate
 * message) and falls back to the generic error toast.
 */
const notifyRejectReasonError = (error?: { data?: { reason?: Array<string> } }) => {
    SnackbarUtils.error(
        error?.data?.reason?.[0] || i18next.t('errors.generalError')
    );
};

export function* fetchCompanyInfoSaga(
    action: PayloadAction<IFetchCompanyInfoAction>
) {
    try {
        const { companyKey } = action.payload;
        const { response } = yield call(fetchCompanyInfo, companyKey);
        if (response?.status === 200) {
            yield put(
                organizationActions.setCompanyInfo({
                    companyInfo: getCompanyInfoFromServerFormat(response.data),
                })
            );
            const defaultLanguage = response.data?.default_language;
            yield call(handleLanguage, defaultLanguage);
        }
    } catch (error) {
        console.log(error);
    }
}

export function* postConnectGmailSaga(
    action: PayloadAction<IPostConnectGmailAction>
) {
    try {
        const { response } = yield call(postConnectGmail, action.payload);
        if (response?.status === 200) {
            yield put(organizationActions.postConnectGmailDone());
            // Refetch company settings to get the newly connected email
            yield put(organizationActions.fetchCompanySettings());
            SnackbarUtils.success(i18next.t('settings.emailSettings.emailConnectedSuccess'));
        }
    } catch (error) {
        console.log(error);
        SnackbarUtils.error(i18next.t('settings.emailSettings.emailConnectedError'));
    } finally {
        yield put(organizationActions.postConnectGmailDone());
    }
}

export function* postConnectOutlookSaga(
    action: PayloadAction<IPostConnectOutlookAction>
) {
    try {
        const { response } = yield call(postConnectOutlook, action.payload);
        if (response?.status === 200) {
            yield put(organizationActions.postConnectOutlookDone());
            // Refetch company settings to get the newly connected email
            yield put(organizationActions.fetchCompanySettings());
            SnackbarUtils.success(i18next.t('settings.emailSettings.emailConnectedSuccess'));
        }
    } catch (error) {
        console.log(error);
        SnackbarUtils.error(i18next.t('settings.emailSettings.emailConnectedError'));
    } finally {
        yield put(organizationActions.postConnectOutlookDone());
    }
}

export function* fetchTagsSaga() {
    try {
        const { response } = yield call(fetchTags);
        if (response?.status === 200) {
            yield put(
                organizationActions.setTags({
                    tags: convertServerTagsToClient(response.data.results),
                })
            );
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(organizationActions.fetchTagsDone());
    }
}

export function* createTagSaga(action: PayloadAction<ICreateTagAction>) {
    try {
        const { response } = yield call(createTag, action.payload);
        if (response?.status === 201) {
            const tags: Array<ITag> = yield select(
                organizationSelectors.getTags
            );
            const newTag = getNewTagOrUpdatedTagFromServerFormat(response.data);
            yield put(
                organizationActions.setTags({
                    tags: [...tags, newTag],
                })
            );
            yield put(organizationActions.createTagDone());
        }
    } catch (error) {
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(organizationActions.createTagDone());
    }
}

export function* updateTagSaga(action: PayloadAction<IUpdateTagAction>) {
    try {
        const { response } = yield call(updateTag, action.payload);
        if (response?.status === 200) {
            const tags: Array<ITag> = yield select(
                organizationSelectors.getTags
            );
            const updatedTag = getNewTagOrUpdatedTagFromServerFormat(
                response.data
            );
            yield put(
                organizationActions.setTags({
                    tags: tags.map((tag) =>
                        tag.tag_id === action.payload.tag_id ? updatedTag : tag
                    ),
                })
            );
            yield put(organizationActions.updateTagDone());
        }
    } catch (error) {
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(organizationActions.updateTagDone());
    }
}

export function* deleteTagSaga(action: PayloadAction<IDeleteTagAction>) {
    try {
        const { id } = action.payload;
        const { response } = yield call(deleteTag, id);
        if (response?.status === 204) {
            const tags: Array<ITag> = yield select(
                organizationSelectors.getTags
            );
            yield put(
                organizationActions.setTags({
                    tags: tags.filter((tag) => tag.tag_id !== id),
                })
            );
        }
    } catch (error) {
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(organizationActions.deleteTagDone());
    }
}

export function* createRejectReasonSaga(
    action: PayloadAction<ICreateRejectReasonAction>
) {
    try {
        const { response, error } = yield call(createRejectReason, action.payload);
        if (response?.status === 201) {
            const rejectReasons: Array<IOption> = yield select(
                organizationSelectors.getRejectApplicationReasons
            );
            yield put(
                organizationActions.setRejectReasons({
                    rejectReasons: [...rejectReasons, response.data],
                })
            );
            yield call(
                SnackbarUtils.success,
                i18next.t('settings.rejectReasons.toast.created')
            );
        } else {
            yield call(notifyRejectReasonError, error);
        }
    } catch (error) {
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(organizationActions.createRejectReasonDone());
    }
}

export function* updateRejectReasonSaga(
    action: PayloadAction<IUpdateRejectReasonAction>
) {
    try {
        const { response, error } = yield call(updateRejectReason, action.payload);
        if (response?.status === 200) {
            const rejectReasons: Array<IOption> = yield select(
                organizationSelectors.getRejectApplicationReasons
            );
            yield put(
                organizationActions.setRejectReasons({
                    rejectReasons: rejectReasons.map((rejectReason) =>
                        rejectReason.value === String(action.payload.id)
                            ? response.data
                            : rejectReason
                    ),
                })
            );
            yield call(
                SnackbarUtils.success,
                i18next.t('settings.rejectReasons.toast.updated')
            );
        } else {
            yield call(notifyRejectReasonError, error);
        }
    } catch (error) {
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(organizationActions.updateRejectReasonDone());
    }
}

export function* deleteRejectReasonSaga(
    action: PayloadAction<IDeleteRejectReasonAction>
) {
    try {
        const { id } = action.payload;
        const { response, error } = yield call(deleteRejectReason, id);
        if (response?.status === 204) {
            const rejectReasons: Array<IOption> = yield select(
                organizationSelectors.getRejectApplicationReasons
            );
            yield put(
                organizationActions.setRejectReasons({
                    rejectReasons: rejectReasons.filter(
                        (rejectReason) => rejectReason.value !== String(id)
                    ),
                })
            );
            yield call(
                SnackbarUtils.success,
                i18next.t('settings.rejectReasons.toast.deleted')
            );
        } else {
            yield call(notifyRejectReasonError, error);
        }
    } catch (error) {
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(organizationActions.deleteRejectReasonDone());
    }
}

export function* fetchCompanySettingsSaga() {
    try {
        const { response } = yield call(fetchCompanySettings);
        if (response?.status === 200) {
            yield put(
                organizationActions.setCompanySettings({
                    companySettings: getCompanySettingsFromServerFormat(
                        response.data
                    ),
                })
            );
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(organizationActions.fetchCompanySettingsDone());
    }
}

export function* updateCompanySettingsSaga(
    action: PayloadAction<IUpdateCompanySettingsAction>
) {
    try {
        const serverData = convertCompanyInfoToServerFormat(action.payload);
        const { response } = yield call(updateCompanySettings, serverData);

        if (response?.status === 200) {
            const companySettings = getCompanySettingsFromServerFormat(
                response.data
            );
            yield put(
                organizationActions.setCompanySettings({
                    companySettings,
                })
            );

            if (companySettings.companyInfo) {
                yield put(
                    organizationActions.setCompanyInfo({
                        companyInfo: companySettings.companyInfo,
                    })
                );

                const myUser = yield select(userSelectors.getMyUser);
                if (myUser) {
                    yield put(
                        userActions.setMyUser({
                            myUser: {
                                ...myUser,
                                company: {
                                    ...myUser.company,
                                    ...companySettings.companyInfo,
                                },
                            },
                        })
                    );
                }
            }

            // Show success message
            yield call(
                SnackbarUtils.success,
                i18next.t('settings.updateSuccess')
            );

            yield put(organizationActions.updateCompanySettingsDone());
        } else {
            yield call(SnackbarUtils.error, i18next.t('settings.updateError'));
        }
    } catch (error) {
        console.error('Error updating company settings:', error);
        yield call(SnackbarUtils.error, i18next.t('settings.updateError'));
    } finally {
        yield put(organizationActions.updateCompanySettingsDone());
    }
}

export function* deleteCompanyMailboxSaga(
    action: PayloadAction<IDeleteCompanyMailboxAction>
) {
    try {
        const { response } = yield call(deleteCompanyMailbox, action.payload);
        if (response?.status === 200) {
            yield put(organizationActions.fetchCompanySettings());
            SnackbarUtils.success(
                i18next.t('settings.emailSettings.emailRemovedSuccess')
            );
        }
    } catch (error) {
        console.log(error);
        SnackbarUtils.error(
            i18next.t('settings.emailSettings.emailRemovedError')
        );
    } finally {
        yield put(organizationActions.deleteCompanyMailboxDone());
    }
}
